import moment from "moment";
import { workDayDateAfter } from "@shyne/shared";
import { AppointmentsRepository } from "@/repositories/appointments.repository";
import { finishedOrCompletedCount } from "@/repositories/appointment-records";
import { SubsRepository } from "@/repositories/subs.repository";
import { DailyWorkRepository, DailyWorkJoin } from "@/repositories/daily-work.repository";
import { appointment_status } from "@/models/appointment.model";
import { daily_work_status } from "@/models/daily-work.model";
import { permissionService } from "@/lib/permissions";

export type VendorDashboardTimeRange = "7d" | "30d" | "90d";

export interface VendorDashboardStats {
  totalSubContractors: number;
  newAppointments: number;
  completedAppointments: number;
  totalAppointments: number;
  activeDailyWorkCount: number;
}

function getDaysFromTimeRange(timeRange: VendorDashboardTimeRange): number {
  return timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
}

export const vendorDashboardApi = {
  async getDashboardStats(timeRange: VendorDashboardTimeRange): Promise<VendorDashboardStats> {
    const days = getDaysFromTimeRange(timeRange);
    const startDate = moment().subtract(days, "days").startOf("day").toDate();
    const endDate = moment().endOf("day").toDate();
    const dateFrom = startDate.toISOString().split("T")[0];
    const dateTo = endDate.toISOString().split("T")[0];

    const userPermissions = await permissionService.getUserPermissions();
    const canAccess = (permission: string) =>
      Boolean(
        userPermissions &&
          (userPermissions.permissions.includes("all") || userPermissions.permissions.includes(permission))
      );
    const canViewSubs = canAccess("subs.view");
    const canViewAppointments = canAccess("appointments.view");
    const canViewDailyWork = canAccess("daily_work.view");

    const [subsCount, appointmentCounts, activeDailyWorkCount] = await Promise.all([
      canViewSubs ? SubsRepository.countByCurrentOrg() : Promise.resolve(0),
      canViewAppointments ? AppointmentsRepository.countsForVendor({ dateFrom, dateTo }) : Promise.resolve(null),
      canViewDailyWork ? DailyWorkRepository.countForVendor(daily_work_status.active) : Promise.resolve(0),
    ]);

    return {
      totalSubContractors: subsCount,
      totalAppointments: appointmentCounts?.total ?? 0,
      newAppointments: appointmentCounts?.byStatus[appointment_status.pending] ?? 0,
      completedAppointments: appointmentCounts ? finishedOrCompletedCount(appointmentCounts.byStatus) : 0,
      activeDailyWorkCount,
    };
  },

  async getActiveDailyWorkList(range: VendorDashboardTimeRange): Promise<{ data: DailyWorkJoin[]; count: number }> {
    const days = getDaysFromTimeRange(range);
    const startDate = workDayDateAfter(0);
    const endDate = workDayDateAfter(days - 1);
    const result = await DailyWorkRepository.listForVendor(
      1,
      50,
      { status: daily_work_status.active, inEffectFrom: startDate, inEffectTo: endDate },
      "created_at",
      "desc"
    );
    return { data: result.data, count: result.count ?? 0 };
  },

  // Reuse existing appointment functions for consistency
  async getPendingAppointments(page: number = 1, pageSize: number = 10) {
    return AppointmentsRepository.listForVendor(
      page,
      pageSize,
      { status: appointment_status.pending },
      "created_at",
      "desc"
    );
  },

  async getInProgressAppointments(page: number = 1, pageSize: number = 10) {
    return AppointmentsRepository.listForVendor(
      page,
      pageSize,
      { status: appointment_status.inprogress },
      "created_at",
      "desc"
    );
  },

  async getTodaysAppointments(page: number = 1, pageSize: number = 20) {
    const today = moment().format("YYYY-MM-DD");
    return AppointmentsRepository.listForVendor(page, pageSize, { dateFrom: today, dateTo: today }, "date", "asc");
  },

  async getWeeklyAppointments(page: number = 1, pageSize: number = 50) {
    const today = moment().format("YYYY-MM-DD");
    const nextWeek = moment().add(7, "days").format("YYYY-MM-DD");
    return AppointmentsRepository.listForVendor(page, pageSize, { dateFrom: today, dateTo: nextWeek }, "date", "asc");
  },
};
